import { useEffect, useState, useRef, useCallback } from 'react';
import { get } from './api.js';

/**
 * reCAPTCHA v2 checkbox hook.
 *
 *   const { containerRef, getToken, reset, ready } = useRecaptcha();
 *   ...
 *   <div ref={containerRef} />
 *   ...
 *   const token = getToken();
 *   if (!token) { setError('Please complete the CAPTCHA.'); return; }
 *   await post('/api/lead', { ...payload, recaptchaToken: token });
 *   reset();
 *
 * Notes:
 *   - Requires v2 site/secret keys (not v3). Issue a new pair at
 *     google.com/recaptcha/admin and set RECAPTCHA_SITE_KEY +
 *     RECAPTCHA_SECRET_KEY on the backend.
 *   - Backend middleware (verifyRecaptcha) reads `recaptchaToken` and handles
 *     v2 tokens transparently — the score check is skipped when Google
 *     doesn't return a score (v2).
 *   - The legacy `action` argument is accepted but ignored (v2 has no
 *     per-action concept) so existing call sites don't need to change.
 */
let scriptLoadingPromise = null;

function loadRecaptchaScript() {
  if (scriptLoadingPromise) return scriptLoadingPromise;
  scriptLoadingPromise = new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve();
    const ready = () => window.grecaptcha && typeof window.grecaptcha.render === 'function';
    if (ready()) return resolve();
    const poll = () => { if (ready()) resolve(); else setTimeout(poll, 50); };
    if (document.getElementById('zvx-recaptcha')) { poll(); return; }
    const s = document.createElement('script');
    s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    s.async = true; s.defer = true; s.id = 'zvx-recaptcha';
    s.onload = poll;
    document.head.appendChild(s);
  });
  return scriptLoadingPromise;
}

export function useRecaptcha(/* action — ignored for v2 */) {
  const [siteKey, setSiteKey] = useState(null);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    get('/api/config')
      .then((c) => { if (!cancelled) setSiteKey(c.recaptchaSiteKey || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!siteKey) return;
    let mounted = true;
    loadRecaptchaScript().then(() => {
      if (!mounted) return;
      if (!containerRef.current) return;
      if (containerRef.current.children.length > 0) return;
      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, { sitekey: siteKey });
      } catch { /* already rendered */ }
    });
    return () => { mounted = false; };
  }, [siteKey]);

  const getToken = useCallback(() => {
    if (typeof window === 'undefined' || !window.grecaptcha || widgetIdRef.current == null) return null;
    try {
      const t = window.grecaptcha.getResponse(widgetIdRef.current);
      return t || null;
    } catch { return null; }
  }, []);

  const reset = useCallback(() => {
    if (typeof window === 'undefined' || !window.grecaptcha || widgetIdRef.current == null) return;
    try { window.grecaptcha.reset(widgetIdRef.current); } catch { /* no-op */ }
  }, []);

  return { containerRef, getToken, reset, ready: !!siteKey };
}
